/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Slobodan Djordjevic
 */
import React, { useState, useEffect } from "react"
import { useHistory, useLocation } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import { ContentSection, SelectPlaylistContent, useToken } from "trux"
import {
    CollectionDocument,
    FindProfileDocument,
    GetMomentCollectionInfoDocument,
    ProfileDocument,
    useAddCollectionItemMutation,
    useAddCollectionMutation,
    useFindProfileQuery,
} from "../../generated/graphql"

export const SaveToPlaylist = (props) => {
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    let moment = useSelector((state) => state.video.value.source)
    if(props.moment) {
        moment = props.moment
    }
    const [collectionsList, setCollectionsList] = useState([])
    const token = useToken("SaveToPlaylist")
    const tokenReady = token != null

    const {
        data: profileData,
        loading: profileLoading,
        error: profileError,
    } = useFindProfileQuery({
        variables: {
            user_id: props?.selectedUser?.user_id,
            token: token,
        },
        skip: !tokenReady,
        onCompleted: (profileData) => {
            if(profileData?.profile?.collections) {
                setCollectionsList([...profileData.profile.collections])
            }
        },
    })

    const [
        addCollectionMutation,
        {
            data: addCollectionMutationData,
            loading: addCollectionMutationLoading,
            error: addCollectionMutationError,
        },
    ] = useAddCollectionMutation({
        variables: {
            token: token,
        },
        refetchQueries: [FindProfileDocument, "findProfile"],
    })

    const [
        addCollectionItem,
        {
            data: addCollectionData,
            loading: addCollectionLoading,
            error: addCollectionError,
        },
    ] = useAddCollectionItemMutation({
        variables: {
            token: token,
        },
        refetchQueries: [
            FindProfileDocument,
            "findProfile",
            CollectionDocument,
            "collection",
            ProfileDocument,
            "profile",
            GetMomentCollectionInfoDocument,
            "getMomentCollectionInfo",
        ],
    })

    const collectionsSortIndex = (collection) => {
        if (collection.title === "Favorites") return 5
        if (collection.title === "Showcase") return 10
        return 20
    }

    useEffect(() => {
        if(collectionsList?.length) {
            collectionsList.sort((a, b) => {
                let aIdx = collectionsSortIndex(a)
                let bIdx = collectionsSortIndex(b)
                let delta = aIdx - bIdx
                if (delta === 0) {
                    delta = new Date(a.creation_time) - new Date(b.creation_time)
                } else if (Math.abs(delta) < 10) {
                    delta = b.title.localeCompare(a.title)
                } else {
                    delta = ["Favorites", "Showcase"].includes(a.title) ? -1 : 1
                }
                return delta
            })
            setCollectionsList(collectionsList)
        }
    }, [collectionsList, collectionsList?.length])

    const createNewPlaylist = () => {
        let profile = profileData?.profile
        let canCreate =
            profile.permission?.create &&
            (profile.permission?.collections_limit === 0 ||
                profile.collections?.length <
                    profile.permission?.collections_limit)
        if (canCreate) {
            if (props.createPlaylist) {
                props.createPlaylist()
            }
        } else {
            props.onClose()
            history.push({
                pathname: location.pathname.replace(
                    "/save/profile",
                    "/goS/upgrade"
                ),
                state: { userId: props?.selectedUser?.user_id },
            })
        }
    }

    const createSuggestedPlaylist = (playlist) => {
        let profile = profileData?.profile
        let canCreate =
            profile.permission?.create &&
            (profile.permission?.collections_limit === 0 ||
                profile.collections?.length <
                    profile.permission?.collections_limit)
        if (canCreate) {
            addCollectionMutation({
                variables: {
                    edit: {
                        title: playlist,
                        public: true,
                        color: playlist === "Homework" ? 6 : 3,
                    },
                    user_playlist_id: profileData?.profile?.user_playlist_id,
                },
            }).then((response) => {
                if (response?.data?.addCollection?.success) {
                    addCollectionItem({
                        variables: {
                            hash_key: response.data.addCollection.hash_key,
                            add: {
                                user_playlist_clip_id: moment.game_clip_id,
                            },
                        },
                    }).then((res) => {
                        if (res?.data?.addCollectionItem?.success) {
                            props.showConfirmation(
                                profile.hash_key,
                                response.data.addCollection.hash_key,
                                res.data.addCollectionItem.user_playlist_clip_id
                            )
                        } else {
                            props.onClose()
                            dispatch({
                                type: "COMMON_SET",
                                key: "toast",
                                value: "Something went wrong",
                            })
                            console.log(
                                "Something went wrong with adding item to created collection"
                            )
                        }
                    })
                } else {
                    props.onClose()
                    dispatch({
                        type: "COMMON_SET",
                        key: "toast",
                        value: "Something went wrong",
                    })
                    console.log("Something went wrong with adding collection")
                }
            })
        } else {
            props.onClose()
            history.push(
                location.pathname.replace("/save/profile", "/goS/upgrade")
            )
        }
    }

    const addMomentToCollection = (collection) => {
        let profile = profileData?.profile
        addCollectionItem({
            variables: {
                hash_key: collection.hash_key,
                add: {
                    user_playlist_clip_id: moment.game_clip_id,
                },
            },
        }).then((res) => {
            if (res?.data?.addCollectionItem?.success) {
                props.showConfirmation(
                    profile.hash_key,
                    collection.hash_key,
                    res.data.addCollectionItem.user_playlist_clip_id
                )
            } else {
                props.onClose()
                dispatch({
                    type: "COMMON_SET",
                    key: "toast",
                    value: "Something went wrong",
                })
                console.error(
                    "Something went wrong with adding item to collection"
                )
            }
        })
    }

    return (
        <ContentSection>
            {collectionsList && (
                <SelectPlaylistContent
                    user={props.selectedUser}
                    collections={collectionsList}
                    createNewPlaylist={() => createNewPlaylist()}
                    createSuggestedPlaylist={(playlist) =>
                        createSuggestedPlaylist(playlist)
                    }
                    addMomentToCollection={(collection) =>
                        addMomentToCollection(collection)
                    }
                    lockPlaylists={profileData?.profile?.permission?.plan_type !== "pro"}
                />
            )}
        </ContentSection>
    )
}
